import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  BlockWithdrawModel,
  TotpRecoveryRequestModel,
  SupportRequestsStatus,
  User, MemoRecoveryRequestModel
} from 'src/app/shared/models/user.model';
import { HttpService } from 'src/app/shared/service/http.service';
import { Card } from 'src/app/shared/models/card.model';
import { map, switchMap } from 'rxjs/operators';
import { Iban } from 'src/app/shared/models/iban.model';
import { Province } from 'src/app/shared/models/province';
import { City } from 'src/app/shared/models/city';
import { Address } from 'src/app/shared/models/address';
import {
  TransferMode,
  WithdrawModel,
  WithdrawRialModel
} from 'src/app/shared/models/withdraw.model';
import { Wallet } from 'src/app/shared/models/wallet.model';
import { UserAssetModel } from 'src/app/shared/models/user-asset.model';
import { Role } from 'src/app/shared/models/role.model';
import { Admin } from 'src/app/shared/models/admin.model';
import { Transfer } from 'src/app/shared/models/transfer.model';
import { Provider, WithdrawRial } from 'src/app/shared/models/withdraw-rial.model';
import { Pagination } from 'src/app/shared/models/pagination.model';
import { Label } from '../models/label.model';
import { Market } from '../models/market.model';
import { Utility } from '../../core/interceptor/utility';
import { WithdrawTreasury } from '../models/withdraw-treasury.model';
import { DipositTreasury } from '../models/deposit-trasury.model';
import { UserTransaction } from '../models/user-transaction.model';
import { UserLimit } from '../models/user-limit.model';
import { HttpClient } from '@angular/common/http';
import { DateModel } from '../models/date.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  userStream = new BehaviorSubject<User>(new User());
  withdrawRequest = new BehaviorSubject<WithdrawModel[]>([]);
  currentUserPage!: number;
  setUserListPage = new BehaviorSubject<number>(1);

  constructor(
    private http: HttpService,
    private httpClient: HttpClient
  ) {
  }

  updatePassword(id: number, password: string): Observable<any> {
    return this.http.request('put', '/v1/admin/user/password-reset', {
      id,
      password
    });
  }

  getAdminUserList(page = 1, limit = 100): Observable<User[]> {
    let savedUsers: User[] = [];
    return this.http.request('get', `/v1/admin?limit=${limit}&page=${page}`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => new User(each))),
      switchMap((users: User[]) => {
        savedUsers = users;
        return this.http.request('get', `/v1/admin?limit=${limit}&page=${2}`);
      }),
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => new User(each))),
      map((users: User[]) => {
        return [...savedUsers, ...users];
      })
    );
  }

  getLabelList(): Observable<Label[]> {
    return this.http.request('get', '/v1/admin/label-definition').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data as Label[])
    );
  }

  personalInformationVerification(
    state: 'ACCEPTED' | 'REJECTED',
    id: number,
    message?: string
  ): Observable<any> {
    return this.http.request('post', '/v1/admin/user/verification/personal-information', {
      id,
      state,
      ...(message ? { message } : {})
    });
  }

  addressVerification(state: 'ACCEPTED' | 'REJECTED', id: number): Observable<any> {
    return this.http.request('post', '/v1/admin/user/verification/address', {
      id,
      state
    });
  }

  identifyPhotoVerification(
    state: 'ACCEPTED' | 'REJECTED',
    id: number,
    message?: string
  ): Observable<any> {
    return this.http.request('post', '/v1/admin/user/verification/identity-photo', {
      id,
      state,
      ...(message ? { message } : {})
    });
  }

  withdrawList(): Observable<WithdrawModel[]> {
    return this.http.request('get', '/v1/admin/user/withdraw/crypto').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => new WithdrawModel(each)))
    );
  }

  withdrawRialList(): Observable<WithdrawModel[]> {
    return this.http.request('get', '/v1/admin/user/withdraw/rial').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => new WithdrawRial(each)))
    );
  }

  updateWithdraw(status: string, id: number, message: string): Observable<any> {
    return this.http.request('put', '/v1/admin/user/withdraw/crypto', {
      id,
      status,
      message
    });
  }

  updateSupportRequestStatus(
    requestId: number,
    status: SupportRequestsStatus,
    description: string | null
  ): Observable<void> {
    return this.http.request('put', '/v1/admin/support-requests', {
      status,
      description,
      id: requestId
    });
  }

  updateWithdrawRial(
    status: string,
    id: number,
    provider: Provider,
    transferMode: TransferMode
  ): Observable<any> {
    return this.http.request('put', '/v1/admin/withdraw/rial', {
      id,
      status,
      provider,
      transferMode
    });
  }

  getProvinceList(): Observable<Province[]> {
    return this.http.request('get', '/v1/province').pipe(map((response: any) => response.data));
  }

  getProvinceCity(id: number): Observable<City[]> {
    return this.http
      .request('get', `/v1/city?province_id=${id}`)
      .pipe(map((response: any) => response.data));
  }

  getUserInfo(id: number): Observable<any> {
    return this.http.request('get', `/v1/admin/user/${id}`).pipe(
      map((response: any) => response.data),
      map((data) => new User(data))
    );
  }

  getUserList(filters: any = {}): Observable<User[]> {
    const searchParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => searchParams.append(key, filters[key]));
    return this.http.request('get', `/v1/admin/user?${searchParams.toString()}`).pipe(
      map((response: any) => response.data),
      map((users: any[]) => users.map((user) => new User(user)))
    );
  }

  getUserListPagination(filters: any = {}): Observable<Pagination<User>> {
    const searchParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => searchParams.append(key, filters[key]));
    return this.http.request('get', `/v1/admin/user?${searchParams.toString()}`).pipe(
      map((response: any) => {
        response.data = response.data.map((user: any) => new User(user));
        return new Pagination<User>(response);
      })
    );
  }

  generateKycReport(startDate: string, endDate: string) {
    return this.httpClient.post(
      'https://team-api.nesoex.com/v1/admin/export-data',

      {
        type: 'unverified_followup',
        start_time: `${startDate} 00:00:00`,
        end_time: `${endDate} 23:59:59`,
        timezone_offset: 210
      },

      {
        responseType: 'blob'
      }
    );
  }

  getUsersCount(): Observable<number> {
    return this.http
      .request('get', `/v1/admin/user`)
      .pipe(map((response: any) => response.total_records));
  }

  getUserListByIban(filters: any = {}): Observable<User[]> {
    const searchParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => searchParams.append(key, filters[key]));
    return this.http.request('get', `/v1/admin/iban/search?${searchParams.toString()}`).pipe(
      map((response: any) => response.data),
      map((users: any[]) =>
        users.map((user) => {
          return new User(user);
        })
      )
    );
  }

  getUserListByCreditCard(filters: any = {}): Observable<User[]> {
    const searchParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => searchParams.append(key, filters[key]));
    return this.http
      .request('get', `/v1/admin/credit-cards/search?${searchParams.toString()}`)
      .pipe(
        map((response: any) => response.data),
        map((users: any[]) => users.map((user) => new User(user)))
      );
  }

  updateUserInfo(id: number, model: any): Observable<any> {
    return this.http.request('put', `/v1/admin/user/${id}`, model);
  }

  getUserCreditCards(id: number): Observable<Card[]> {
    return this.http.request('get', `/v1/admin/credit-cards/${id}`).pipe(
      map((response: any) => response.data),
      map((creditCards: []) => creditCards?.map((card) => new Card(card)))
    );
  }

  setCreditCardStatus(id: number, status: string): Observable<any> {
    return this.http.request('put', `/v1/admin/credit-cards/${id}`, {
      status
    });
  }

  getUserIbanList(id: number): Observable<Iban[]> {
    return this.http.request('get', `/v1/admin/iban/${id}`).pipe(
      map((response: any) => response.data),
      map((ibans: []) => ibans?.map((iban) => new Iban(iban)))
    );
  }

  setIbanStatus(id: number, status: string): Observable<any> {
    return this.http.request('put', `/v1/admin/iban/${id}`, {
      status
    });
  }

  deleteCreditCard(id: number): Observable<any> {
    return this.http.request('delete', `/v1/admin/credit-cards/${id}`);
  }

  deleteIban(id: number): Observable<any> {
    return this.http.request('delete', `/v1/admin/iban/${id}`);
  }

  getImpersonateToken(id: number): Observable<any> {
    return this.http.request('post', `/v1/admin/user/${id}/impersonate`, {});
  }

  getUserRawWallets(userId: number[]): Observable<UserAssetModel[]> {
    return this.http
      .request('post', '/v1/admin/wallet', {
        userId
      })
      .pipe(
        map((response: any) => response.data),
        map((assets: any[]) => assets.map((asset) => new UserAssetModel(asset)))
      );
  }

  getMarkets(): Observable<Market[]> {
    return this.http.request('get', '/v1/market').pipe(
      map((response: any) => response.data),
      map((markets: any[]) => markets.map((market) => Utility.camelize(market) as Market))
    );
  }

  getMarketCurrencyIRTAndUSDTMap(markets: Market[]) {
    const irtCurrencyMarketMap = new Map<string, Market>();
    const usdtCurrencyMarketMap = new Map<string, Market>();
    for (const market of markets) {
      if (market.quoteCurrency.id === 'IRR') {
        irtCurrencyMarketMap.set(market.baseCurrency.id, market);
      }
      if (market.quoteCurrency.id === 'USDT') {
        usdtCurrencyMarketMap.set(market.baseCurrency.id, market);
      }
    }
    return [irtCurrencyMarketMap, usdtCurrencyMarketMap];
  }

  fixWalletIRRAndUSDTPriceValue(wallet: Wallet, usdtMarket?: Market, irtMarket?: Market): Wallet {
    wallet.currency.usdcPrice = usdtMarket?.lastPrice ?? 1;
    wallet.currency.usdtPrice = wallet.currency.usdcPrice;
    wallet.currency.irrPrice = irtMarket?.lastPrice ?? 1;
    wallet.currency.irrSellPrice = Math.floor(wallet.currency.irrPrice / 10);
    wallet.currency.irrBuyPrice = Math.floor(wallet.currency.irrPrice / 10);
    wallet.currency.irrPrice = Math.floor(wallet.currency.irrPrice / 10);
    return wallet;
  }

  getUserWallets(userIds: number[]): Observable<UserAssetModel[]> {
    return combineLatest([this.getMarkets(), this.getUserRawWallets(userIds)]).pipe(
      map(([markets, usersWallets]) => {
        const [irtCurrencyMarketMap, usdtCurrencyMarketMap] =
          this.getMarketCurrencyIRTAndUSDTMap(markets);
        for (const users of usersWallets) {
          for (let wallet of users.wallet) {
            wallet = this.fixWalletIRRAndUSDTPriceValue(
              wallet,
              usdtCurrencyMarketMap.get(wallet.currency.id),
              irtCurrencyMarketMap.get(wallet.currency.id)
            );
          }
        }
        return usersWallets;
      })
    );
  }

  getWalletExcludedUserId(excludedUserId: number[]): Observable<Wallet[]> {
    return this.http
      .request('post', '/v1/admin/wallet/summarize', {
        excludedUserId
      })
      .pipe(
        map((response: any) => response.data),
        map((wallets: any[]) => wallets.map((wallet) => new Wallet(wallet)))
      );
  }

  getWalletSummarize(excludedUserId: number[]): Observable<Wallet[]> {
    return combineLatest([this.getMarkets(), this.getWalletExcludedUserId(excludedUserId)]).pipe(
      map(([markets, wallets]) => {
        const [irtCurrencyMarketMap, usdtCurrencyMarketMap] =
          this.getMarketCurrencyIRTAndUSDTMap(markets);
        for (let wallet of wallets) {
          wallet = this.fixWalletIRRAndUSDTPriceValue(
            wallet,
            usdtCurrencyMarketMap.get(wallet.currency.id),
            irtCurrencyMarketMap.get(wallet.currency.id)
          );
        }
        return wallets;
      })
    );
  }

  getTransferHistory(
    page: number,
    limit = 10,
    userIds: number[]
  ): Observable<Pagination<Transfer>> {
    return this.http
      .request('post', `/v2/admin/wallet/transfer?page=${page}&limit=${limit}`, {
        userId: userIds
      })
      .pipe(
        map((response: any) => {
          response.data = response.data.map((history: unknown) => new Transfer(history));
          return response;
        }),
        map((response) => new Pagination<Transfer>(response))
      );
  }

  getAdminUserLevels(): Observable<Role[]> {
    return this.http.request('get', '/v1/admin/roles').pipe(map((response: any) => response.data));
  }

  createAdminUser(admin: Admin): Observable<any> {
    return this.http.request('post', '/v1/admin/user', admin);
  }

  updateAdminUser(id: number, admin: Admin): Observable<any> {
    return this.http.request('put', `/v1/admin/user/admin/${id}`, admin);
  }

  getTransactions(filters: any): Observable<any> {
    filters.timezoneOffset = new Date().getTimezoneOffset() * -1;
    return this.http.request('post', '/v2/admin/transaction', filters);
  }

  getUserTransactions(filters: any): Observable<Pagination<UserTransaction>> {
    return this.http.request('post', '/v2/admin/transaction', filters).pipe(
      map((response: any) => {
        response.data = response.data?.map((order: any) => new UserTransaction(order));
        return response;
      }),
      map((response) => new Pagination<UserTransaction>(response))
    );
  }

  getTreasuryWithdraw(filters: any): Observable<any> {
    filters.timezoneOffset = new Date().getTimezoneOffset() * -1;
    return this.http.request('post', '/v1/admin/withdraw/rial/treasury', filters).pipe(
      map((response: any) => {
        response.data = response.data?.map((order: any) => new WithdrawTreasury(order));
        return response;
      }),
      map((response) => new Pagination<WithdrawTreasury>(response))
    );
  }

  getTreasuryDiposit(filters: any): Observable<any> {
    filters.timezoneOffset = new Date().getTimezoneOffset() * -1;
    return this.http.request('post', '/v1/admin/deposit/rial/treasury', filters).pipe(
      map((response: any) => {
        response.data = response.data?.map((order: any) => new DipositTreasury(order));
        return response;
      }),
      map((response) => new Pagination<DipositTreasury>(response))
    );
  }

  setWithdrawAlert(alert: { show: boolean; silent: boolean }): void {
    localStorage.setItem('withdrawAlert', JSON.stringify(alert));
  }

  transferMoney(data: any): Observable<any> {
    return this.http.request('post', '/v1/admin/wallet/transfer', data);
  }

  changeUserLabel(labels: number[], userId: number): Observable<any> {
    return this.http.request('post', `/v1/admin/user/${userId}/label`, {
      labels
    });
  }

  getUserLabel(id: number): Observable<Label[]> {
    return this.http.request('get', `/v1/admin/user/${id}/label`).pipe(
      map((response: any) => response.data),
      map((data: any[]) => data as Label[])
    );
  }

  getUserWithdrawLimits(id: number): Observable<UserLimit[]> {
    return this.http
      .request('get', `/v1/admin/user/withdraw/limit`, {
        user_id: id
      })
      .pipe(map((data: { data: UserLimit[]; status: string }) => data.data));
  }

  putUserWithdrawLimits(data: UserLimit): Observable<void> {
    return this.http.request('put', `/v1/admin/user/withdraw/limit`, data);
  }

  getUserDepositRecoveryRequests(
    status: SupportRequestsStatus,
    page: number,
    limit: number = 50,
    type : 'RECOVERY_MEMO_REQUEST' | 'RECOVERY_MEMO_VERIFICATION',
  ): Observable<any> {
    return this.http
      .request('get', `/v1/admin/support-requests?status=${status}&page=${page}&limit=${limit}&type=${type}`)
      .pipe(
        map((response: any) => {
          response.data = response.data.map((each: any) => new MemoRecoveryRequestModel(each));
          return response;
        }),
        map((data) => new Pagination<MemoRecoveryRequestModel>(data))
      );
  }

  getUserTotpRecoveryRequests(
    status: SupportRequestsStatus,
    page: number,
    limit: number = 50
  ): Observable<any> {
    return this.http
      .request('get', `/v1/admin/support-requests?status=${status}&page=${page}&limit=${limit}&type=RECOVER_2FA`)
      .pipe(
        map((response: any) => {
          response.data = response.data.map((each: any) => new TotpRecoveryRequestModel(each));
          return response;
        }),
        map((data) => new Pagination<TotpRecoveryRequestModel>(data))
      );
  }

  getWithdrawAlert(): { show: boolean; silent: boolean } {
    if (localStorage.getItem('withdrawAlert')) {
      return JSON.parse(localStorage.getItem('withdrawAlert') ?? '{}');
    } else {
      this.setWithdrawAlert({
        show: true,
        silent: false
      });
      return {
        show: true,
        silent: false
      };
    }
  }

  getBlockWithdrawRequest() {
    return this.http.request('get', '/v1/admin/user/withdraw/block-status').pipe(
      map((response: any) => response.data),
      map((data: any[]) => data.map((each) => new BlockWithdrawModel(each)))
    );
  }

  setBlockWithdraw(user: User, reason: string, expireAt?: string) {
    return this.http.request('post', '/v1/admin/user/withdraw/block-status', {
      reason: reason,
      userId: user.id,
      expireAt: expireAt ? expireAt : null
    });
  }

  deleteBlockWithdraw(id: number) {
    return this.http.request('delete', '/v1/admin/user/withdraw/block-status?id=' + id);
  }
}
